import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { getBoundingBoxFromMap } from './map';
import { setMapBoundingBox } from '../../actions/mapV2';
// REMOVE: import useMap from '../../hooks/map/useMap'; // Not used as map is a prop

/**
 * @description
 * We use the bounding box of the map for fetching items within the map bounds
 * so we want to sync it to the store. This version ties updates directly
 * to map events for better reliability.
 * @param {import('@types/leaflet').Map | null | undefined} map The Leaflet map instance.
 */
const useSyncMapBoundingBoxToStore = (map) => {
  const dispatch = useDispatch();
  const mountedRef = useRef(true);

  // Track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const updateBounds = () => {
      // Only update if component is still mounted
      if (!mountedRef.current) {
        return;
      }

      const bbox = getBoundingBoxFromMap(map);
      if (bbox) {
        dispatch(setMapBoundingBox(bbox));
      }
    };

    map.on('load', updateBounds);
    map.on('moveend', updateBounds);

    // Check if the map has already loaded and is ready for operations

    const isMapLoaded = Object.prototype.hasOwnProperty.call(map, '_loaded') && map._loaded;

    const hasContainer = Object.prototype.hasOwnProperty.call(map, '_container');

    const hasParentNode = hasContainer && map._container && map._container.parentNode;

    const isMapReady = isMapLoaded && hasParentNode && typeof map.getBounds === 'function';

    if (isMapReady) {
      // Add a slight delay to ensure the map is fully rendered before getting bounds
      setTimeout(() => {
        // Double-check that map is still valid and component is mounted when timeout executes
        if (mountedRef.current && map && map.getBounds && typeof map.getBounds === 'function') {
          updateBounds();
        }
      }, 0);
    }

    return () => {
      if (map && typeof map.off === 'function') {
        map.off('load', updateBounds);
        map.off('moveend', updateBounds);
      }
    };
  }, [map, dispatch]);

  return null;
};

export default useSyncMapBoundingBoxToStore;
